/**
 * useSEO Hook
 *
 * React hook for managing page SEO metadata and schema markup.
 * Automatically updates meta tags, title, and injects schema when component mounts.
 *
 * Usage:
 * ```tsx
 * import { useSEO } from '../hooks/useSEO'
 *
 * function MyPage() {
 *   useSEO('/dashboard')
 *   return <div>...</div>
 * }
 * ```
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { getPageSEO, updateMetaTags, injectSchemaMarkup } from '../lib/seo'

/**
 * Hook to manage SEO for the current page
 * @param pathname - Optional pathname override (defaults to current route)
 * @param options - Optional fallback behavior for routes without configured metadata
 */
export function useSEO(pathname?: string, options?: { noindexWhenMissing?: boolean }) {
  const location = useLocation()
  const currentPath = pathname || location.pathname
  const noindexWhenMissing = options?.noindexWhenMissing ?? false

  useEffect(() => {
    const seo = getPageSEO(currentPath)

    if (!seo) {
      // Fallback to default meta if no specific config
      // CRITICAL: We must still update the canonical to self-reference
      // otherwise it will keep the previous page's canonical!
      const fallbackSeo = {
        title: 'Optioneer — Options Portfolio Tracker',
        description: 'Options portfolio tracker for retail traders.',
        canonical: `https://optioneer.io${currentPath}`,
        noindex: noindexWhenMissing,
      }
      updateMetaTags(fallbackSeo)
      injectSchemaMarkup(undefined)
      return
    }

    // Update meta tags
    updateMetaTags(seo)

    // Inject schema markup
    injectSchemaMarkup(seo.schema)
  }, [currentPath, location, noindexWhenMissing])
}

/**
 * Hook for custom SEO (useful for dynamic pages)
 * @param title - Custom page title
 * @param description - Custom description
 * @param additionalMeta - Additional meta tags
 */
export function useCustomSEO(
  title: string,
  description: string,
  additionalMeta?: {
    keywords?: string[]
    image?: string
    noindex?: boolean
    canonical?: string
    schema?: Array<Record<string, unknown>>
  }
) {
  const location = useLocation()

  useEffect(() => {
    const seo = {
      title,
      description,
      keywords: additionalMeta?.keywords,
      canonical: additionalMeta?.canonical || `https://optioneer.io${location.pathname}`,
      og: {
        title,
        description,
        type: 'website',
        image: additionalMeta?.image,
      },
      twitter: {
        card: 'summary_large_image' as const,
        title,
        description,
        image: additionalMeta?.image,
      },
      noindex: additionalMeta?.noindex || false,
    }

    updateMetaTags(seo)
    injectSchemaMarkup(additionalMeta?.schema)
  }, [title, description, additionalMeta, location.pathname])
}
